"use client"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronLeft, Archive, Mail, Calendar, Trash2, RotateCcw } from "lucide-react"
import TokenChecker from "../TokenChecker"

const ARCHIVE_RETENTION_DAYS = 30

const formatDateTime = (value) => {
  if (!value) return null
  try {
    return new Date(value).toLocaleString()
  } catch (error) {
    return value
  }
}

const addDays = (value, days) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  date.setDate(date.getDate() + days)
  return date.toISOString()
}

export default function FlowSyncArchive() {
  const [activeTab, setActiveTab] = useState("tasks")
  const [archivedTasks, setArchivedTasks] = useState([])
  const [archivedDrafts, setArchivedDrafts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionMessage, setActionMessage] = useState(null)
  const [processingTaskIds, setProcessingTaskIds] = useState([])
  const [processingDraftIds, setProcessingDraftIds] = useState([])
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const navigate = useNavigate()

  const URL_PreFix = "/api/flowsync/"

  useEffect(() => {
    const fetchArchive = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await TokenChecker(`${URL_PreFix}archive/`, {
          method: "GET",
          headers: {
            'Content-Type': 'application/json'
          }
        })
        const payload = await response.json().catch(() => ({}))
        if (!response.ok) {
          throw new Error(payload.error || "Failed to load FlowSync archive.")
        }
        setArchivedTasks(payload.archived_tasks || [])
        setArchivedDrafts(payload.archived_drafts || [])
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load FlowSync archive.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchArchive()
  }, [URL_PreFix])

  const handleUnarchiveTask = async (taskId) => {
    if (!taskId) return
    setError(null)
    setActionMessage(null)
    setProcessingTaskIds((prev) => (prev.includes(taskId) ? prev : [...prev, taskId]))
    try {
      const response = await TokenChecker(`${URL_PreFix}archive/tasks/${taskId}/unarchive/`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        }
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Failed to unarchive task.")
      }
      setArchivedTasks((prev) => prev.filter((task) => task.id !== taskId))
      setActionMessage(payload.message || "Task restored to review.")
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to unarchive task.")
    } finally {
      setProcessingTaskIds((prev) => prev.filter((id) => id !== taskId))
    }
  }

  const handleDeleteArchivedTask = async (taskId) => {
    if (!taskId) return
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Permanently delete this archived task?")) return
    setError(null)
    setActionMessage(null)
    setProcessingTaskIds((prev) => (prev.includes(taskId) ? prev : [...prev, taskId]))
    try {
      const response = await TokenChecker(`${URL_PreFix}archive/tasks/${taskId}/delete/`, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json'
        }
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Failed to delete archived task.")
      }
      setArchivedTasks((prev) => prev.filter((task) => task.id !== taskId))
      setActionMessage(payload.message || "Archived task deleted.")
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to delete archived task.")
    } finally {
      setProcessingTaskIds((prev) => prev.filter((id) => id !== taskId))
    }
  }

  const handleUnarchiveDraft = async (draftId) => {
    if (!draftId) return
    setError(null)
    setActionMessage(null)
    setProcessingDraftIds((prev) => (prev.includes(draftId) ? prev : [...prev, draftId]))
    try {
      const response = await TokenChecker(`${URL_PreFix}archive/responses/${draftId}/unarchive/`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        }
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Failed to unarchive response.")
      }
      setArchivedDrafts((prev) => prev.filter((draft) => draft.id !== draftId))
      setActionMessage(payload.message || "Response restored to review.")
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to unarchive response.")
    } finally {
      setProcessingDraftIds((prev) => prev.filter((id) => id !== draftId))
    }
  }

  const handleDeleteArchivedDraft = async (draftId) => {
    if (!draftId) return
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Permanently delete this archived response?")) return
    setError(null)
    setActionMessage(null)
    setProcessingDraftIds((prev) => (prev.includes(draftId) ? prev : [...prev, draftId]))
    try {
      const response = await TokenChecker(`${URL_PreFix}archive/responses/${draftId}/delete/`, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json'
        }
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Failed to delete archived response.")
      }
      setArchivedDrafts((prev) => prev.filter((draft) => draft.id !== draftId))
      setActionMessage(payload.message || "Archived response deleted.")
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to delete archived response.")
    } finally {
      setProcessingDraftIds((prev) => prev.filter((id) => id !== draftId))
    }
  }

  const handleDeleteAllArchivedTasks = async () => {
    if (archivedTasks.length === 0 || isBulkDeleting) return
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Permanently delete all archived tasks?")) return
    setError(null)
    setActionMessage(null)
    setIsBulkDeleting(true)
    try {
      const response = await TokenChecker(`${URL_PreFix}archive/tasks/delete-all/`, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json'
        }
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Failed to delete archived tasks.")
      }
      setArchivedTasks([])
      setActionMessage(payload.message || "Archived tasks deleted.")
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to delete archived tasks.")
    } finally {
      setIsBulkDeleting(false)
    }
  }

  const handleDeleteAllArchivedDrafts = async () => {
    if (archivedDrafts.length === 0 || isBulkDeleting) return
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Permanently delete all archived responses?")) return
    setError(null)
    setActionMessage(null)
    setIsBulkDeleting(true)
    try {
      const response = await TokenChecker(`${URL_PreFix}archive/responses/delete-all/`, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json'
        }
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload.error || "Failed to delete archived responses.")
      }
      setArchivedDrafts([])
      setActionMessage(payload.message || "Archived responses deleted.")
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to delete archived responses.")
    } finally {
      setIsBulkDeleting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-4xl font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent">
              <ChevronLeft className="inline-block w-6 h-6 mr-6 cursor-pointer text-[#10A7F3]" onClick={() => navigate("/flowsync")} />
              Archive - Flowsync
            </span>
            <p className="text-sm ml-8 text-gray-500">
              Deleted after {ARCHIVE_RETENTION_DAYS} days
            </p>
          </div>
          <button
            onClick={() => navigate("/flowsync")}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Archive className="w-4 h-4" />
            Back to FlowSync
          </button>
        </div>
      </header>

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab("tasks")}
              className={`py-4 px-2 border-b-2 text-sm font-medium transition-colors ${
                activeTab === "tasks"
                  ? "border-gray-900 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Archived Tasks
            </button>
            <button
              onClick={() => setActiveTab("responses")}
              className={`py-4 px-2 border-b-2 text-sm font-medium transition-colors ${
                activeTab === "responses"
                  ? "border-gray-900 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Archived Responses
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-8 py-8 space-y-6">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {actionMessage && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {actionMessage}
          </div>
        )}

        {isLoading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
            Loading archive…
          </div>
        ) : activeTab === "tasks" ? (
          archivedTasks.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <Archive className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No archived tasks</p>
              <p className="text-sm text-gray-400 mt-2">
                Deleted suggestions will appear here for {ARCHIVE_RETENTION_DAYS} days.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={handleDeleteAllArchivedTasks}
                  disabled={isBulkDeleting}
                  className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                    isBulkDeleting
                      ? "border-red-100 text-red-300 bg-red-50 cursor-not-allowed"
                      : "border-red-200 text-red-600 hover:bg-red-50"
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                  {isBulkDeleting ? "Deleting all…" : "Delete all"}
                </button>
              </div>
              {archivedTasks.map((task) => {
                const isProcessing = processingTaskIds.includes(task.id)
                return (
                  <article
                    key={task.id}
                    className="bg-white shadow-sm rounded-md border border-gray-200 p-6"
                  >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Archived Task
                      </p>
                      <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
                      <p className="text-sm text-gray-500">
                        From {task.source_email_sender || "Unknown sender"}
                      </p>
                    </div>
                    <div className="text-right text-xs text-gray-400 space-y-1">
                      <div className="flex items-center justify-end gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>Due {formatDateTime(task.due_date) || "No due date"}</span>
                      </div>
                      <p>Archived {formatDateTime(task.archived_at) || "recently"}</p>
                      <p>
                        Auto-delete {formatDateTime(addDays(task.archived_at, ARCHIVE_RETENTION_DAYS)) || "soon"}
                      </p>
                      <div className="flex flex-wrap justify-end gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => handleUnarchiveTask(task.id)}
                          disabled={isProcessing}
                          className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                            isProcessing
                              ? "border-blue-100 text-blue-300 bg-blue-50 cursor-not-allowed"
                              : "border-blue-200 text-blue-700 hover:bg-blue-50"
                          }`}
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          Unarchive
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteArchivedTask(task.id)}
                          disabled={isProcessing}
                          className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                            isProcessing
                              ? "border-red-100 text-red-300 bg-red-50 cursor-not-allowed"
                              : "border-red-200 text-red-600 hover:bg-red-50"
                          }`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                  {task.description && (
                    <div className="mt-4 bg-gray-50 text-sm text-gray-700 p-4 rounded-md whitespace-pre-line">
                      {task.description}
                    </div>
                  )}
                  <div className="mt-4 text-xs text-gray-500">
                    Source subject: {task.source_email_subject || "Unknown subject"}
                  </div>
                  </article>
                )
              })}
            </div>
          )
        ) : archivedDrafts.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No archived responses</p>
            <p className="text-sm text-gray-400 mt-2">
              Deleted drafts will appear here for {ARCHIVE_RETENTION_DAYS} days.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={handleDeleteAllArchivedDrafts}
                disabled={isBulkDeleting}
                className={`inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
                  isBulkDeleting
                    ? "border-red-100 text-red-300 bg-red-50 cursor-not-allowed"
                    : "border-red-200 text-red-600 hover:bg-red-50"
                }`}
              >
                <Trash2 className="w-4 h-4" />
                {isBulkDeleting ? "Deleting all…" : "Delete all"}
              </button>
            </div>
            {archivedDrafts.map((draft) => {
              const isProcessing = processingDraftIds.includes(draft.id)
              return (
                <article
                  key={draft.id}
                  className="bg-white shadow-sm rounded-md border border-gray-200 p-6"
                >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Archived Response
                    </p>
                    <h3 className="text-lg font-semibold text-gray-900">{draft.subject || "Untitled"}</h3>
                    <p className="text-sm text-gray-500">
                      To {draft.recipient || "Unknown recipient"}
                    </p>
                  </div>
                  <div className="text-right text-xs text-gray-400 space-y-1">
                    <p>Archived {formatDateTime(draft.archived_at) || "recently"}</p>
                    <p>
                      Auto-delete {formatDateTime(addDays(draft.archived_at, ARCHIVE_RETENTION_DAYS)) || "soon"}
                    </p>
                    <div className="flex flex-wrap justify-end gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => handleUnarchiveDraft(draft.id)}
                        disabled={isProcessing}
                        className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                          isProcessing
                            ? "border-blue-100 text-blue-300 bg-blue-50 cursor-not-allowed"
                            : "border-blue-200 text-blue-700 hover:bg-blue-50"
                        }`}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Unarchive
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteArchivedDraft(draft.id)}
                        disabled={isProcessing}
                        className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                          isProcessing
                            ? "border-red-100 text-red-300 bg-red-50 cursor-not-allowed"
                            : "border-red-200 text-red-600 hover:bg-red-50"
                        }`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-600 whitespace-pre-line bg-gray-50 rounded-md p-4">
                  {draft.body || "No draft body available."}
                </div>
                <div className="mt-4 flex flex-wrap gap-3 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 text-blue-600">
                    Original subject: {draft.original_subject || "Unknown subject"}
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 text-blue-600">
                    From: {draft.original_sender || "Unknown sender"}
                  </span>
                </div>
                </article>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}
